import { HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { map } from "rxjs";

@Injectable()
export class DateInterceptor implements HttpInterceptor {
  private isoLocalDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;
  private isoUtcDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    if (req.body) {
      let body = req.body;

      if (body instanceof FormData) {
        body = this.convertFormDataDatesToUTC(body);
      } else if (this.isObjectOrArray(body)) {
        body = this.convertDatesToUTC(body);
      }

      req = req.clone({ body });
    }

    return next.handle(req).pipe(
      map((event) => {
        if (event instanceof HttpResponse && event.body) {
          return event.clone({ body: this.convertUTCToDates(event.body) });
        }
        return event;
      }),
    );
  }

  private convertDatesToUTC(obj: any): any {
    if (obj === null || obj === undefined) return obj;

    if (obj instanceof Date) {
      return this.safeToISOString(obj) ?? obj;
    }

    if (typeof obj === "string") {
      if (this.isoUtcDateRegex.test(obj)) return obj;
      if (this.isoLocalDateRegex.test(obj)) {
        return this.safeToISOString(new Date(obj)) ?? obj;
      }
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.convertDatesToUTC(item));
    }

    if (typeof obj === "object") {
      const converted: any = {};
      for (const key of Object.keys(obj)) {
        converted[key] = this.convertDatesToUTC(obj[key]);
      }
      return converted;
    }

    return obj;
  }

  private convertUTCToDates(obj: any): any {
    if (obj === null || obj === undefined || typeof obj !== "object")
      return obj;

    if (obj instanceof Date) return obj;

    if (Array.isArray(obj)) {
      return obj.map((item) => this.convertUTCToDates(item));
    }

    const result: any = {};
    for (const key of Object.keys(obj)) {
      const value = obj[key];

      if (
        typeof value === "string" &&
        (this.isoUtcDateRegex.test(value) || this.isoLocalDateRegex.test(value))
      ) {
        const date = new Date(value);
        result[key] = isNaN(date.getTime()) ? value : date;
      } else {
        result[key] = this.convertUTCToDates(value);
      }
    }
    return result;
  }

  private convertFormDataDatesToUTC(formData: FormData): FormData {
    const newFormData = new FormData();

    formData.forEach((value, key) => {
      if (value instanceof File) {
        newFormData.append(key, value);
        return;
      }

      if (typeof value === "string" && this.isoUtcDateRegex.test(value)) {
        newFormData.append(key, value);
        return;
      }

      if (typeof value === "string" && this.isoLocalDateRegex.test(value)) {
        const iso = this.safeToISOString(new Date(value));
        newFormData.append(key, iso ?? value);
        return;
      }

      newFormData.append(key, value);
    });

    return newFormData;
  }

  private safeToISOString(date: Date): string | null {
    if (isNaN(date.getTime())) return null;
    return date.toISOString();
  }

  private isObjectOrArray(value: any): boolean {
    return value !== null && typeof value === "object";
  }
}
